// MACHINERY V2 (Stroje) — Fleet V1 big card (M2.2).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.2 + §5 #9 (data parity).
// Reference impl: design_handoff_machinery_v3/machinery-v1-big.jsx (V1BigCard,
// KV) + machinery-live.jsx atoms.
//
// COMPOSITION (artboard): photo + capability badges -> identity + kebab ->
// status row -> KV identity grid -> chips -> exactly ONE footer.
//
// STATUS RENDERING IS NOT DECIDED HERE. Every status paint comes from
// deriveCardPresentation(telemetry): this component reads its flags and never
// re-tests `alarms.length`, `conn` or `status`. That is the M2.1 contract, and
// the split brain it prevents (a RUNNING badge above a red alarm strip) has
// already shipped once. The connectivity dot takes `presentation.dotConn`, NOT
// the raw `conn` — see the dotConn note in cardStates.ts.
//
// THE DESIGNER'S RotatingFooter IS DELIBERATELY NOT PORTED. It cross-fades
// alarm / program / "last service report" panels every 3 s, but the grid DTO
// carries NO last-service-report object (data package §9 lists only
// openServiceReportsCount, which feeds the health dot). With no third panel
// there is nothing to rotate, and inventing an SR line would be fabricated
// data. The footer therefore renders the contract's single panel, exactly as
// StateCard does. Revisit if a last-SR field is added to the machine summary.
//
// CARRIED LIVE-GRID CHROME (data package §9): NEW badge, health dot, group
// pills, S/N-missing warning, decommissioned treatment (all in
// FleetCardMeta / fleetCardAtoms) and the 4-tier image fallback via the SHARED
// useGalleryImages — the same chain the live cards use, not a re-implementation.
// The SR-request indicator badge stays OUT: it is hidden on every live grid
// variant today (the grid passes hideServiceBadge) and §9 D4 did not rule on it.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useGalleryImages } from "../../../../shared/components/gallery";
import {
  getBrandDisplayName,
  getMachineDisplayName,
  getMachineName,
  getProductionYearDisplay,
} from "../../../../shared/utils/machineUtils";
import { Machine } from "../../models/models";
import { CapabilityBadges } from "../readiness/CapabilityBadge";
import { MachineryV2ReadinessPresentation } from "../readiness/readinessPresentation";
import AlarmStrip from "./AlarmStrip";
import ConnDot from "./ConnDot";
import FleetCardMenu, { FleetMenuAction } from "./FleetCardMenu";
import { FleetCardChips, FleetCardDecommissionNote } from "./FleetCardMeta";
import ProgramLine from "./ProgramLine";
import StatusBadge, { AwaitingBadge } from "./StatusBadge";
import { NoTelemetryFooter } from "./StateCard";
import { HealthDot, KV, MachinePhoto, NewBadge, PinGlyph } from "./fleetCardAtoms";
import { MachineryV2Telemetry, deriveCardPresentation } from "./cardStates";

export interface FleetCardProps {
  machine: Machine;
  telemetry: MachineryV2Telemetry;
  /** Null for a CUSTOM machine (no catalog model) or a decommissioned one —
   *  the live grid hides the readiness surface in exactly those two cases. A
   *  catalog model with NO readiness data still gets a presentation, so its
   *  badges render UNLIT rather than disappearing (the M2.9 absence rule). */
  readiness: MachineryV2ReadinessPresentation | null;
  isNew: boolean;
  actions: FleetMenuAction[];
  onOpenProfile: () => void;
  onOpenReadiness: () => void;
}

const FleetCard: React.FC<FleetCardProps> = ({
  machine, telemetry, readiness, isNew, actions, onOpenProfile, onOpenReadiness,
}) => {
  const { translate } = useTranslation("myMachinery");
  const p = deriveCardPresentation(telemetry);
  const alarm = p.showAlarmStrip ? telemetry.alarms[0] : null;
  const decommissioned = !!machine.isDecommissioned;
  const snMissing = !machine.serialNumber && !decommissioned;

  // The SHARED 4-tier fallback: machine files -> machine.image -> catalog hero
  // -> catalog image rows.
  const images = useGalleryImages({
    type: "machine",
    image: machine.image?.content || null,
    catalogImageUrl:
      machine.catalogModel?.imageStandardUrl || machine.catalogModel?.imageUrl || null,
    catalogImages: machine.catalogModel?.images || [],
    files: machine.files?.map((f) => ({ name: f.name, content: f.content })) || [],
  });

  const location = machine.locationName
    ? machine.parentLocationName
      ? machine.parentLocationName + " › " + machine.locationName
      : machine.locationName
    : null;
  const year = getProductionYearDisplay(machine);

  return (
    <article
      onClick={onOpenProfile}
      data-machine-id={machine.id}
      style={{
        position: "relative", background: "var(--card)", borderRadius: 12,
        border: decommissioned
          ? "1px dashed var(--border)"
          : "1px solid " + (snMissing ? "var(--amber)" : "var(--border)"),
        // overflow visible so the kebab menu and tooltips escape the card.
        overflow: "visible", boxShadow: "var(--shadow)", display: "flex",
        flexDirection: "column", width: "100%", minWidth: 0, cursor: "pointer",
        opacity: decommissioned ? 0.65 : 1,
      }}
    >
      <div style={{ padding: "16px 16px 14px", display: "flex", gap: 14, minWidth: 0 }}>
        {/* Photo + capability badges (M2.9 kit) overlaid upper-left */}
        <div style={{ position: "relative", flexShrink: 0 }}>
          <MachinePhoto
            src={images.length > 0 ? images[0].src : null}
            alt={getMachineDisplayName(machine)}
            grayscale={decommissioned}
          />
          {readiness && (
            <div style={{ position: "absolute", top: 6, left: 6 }}>
              <CapabilityBadges
                presentation={readiness}
                size={20}
                onOpenDetails={onOpenReadiness}
              />
            </div>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, minWidth: 0 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 600, fontSize: 14.5, color: "var(--ink)", lineHeight: 1.25,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}
              >
                {getMachineDisplayName(machine)}
              </div>
              <div
                style={{
                  fontSize: 12, color: "var(--muted)", marginTop: 2, whiteSpace: "nowrap",
                  overflow: "hidden", textOverflow: "ellipsis",
                }}
              >
                {getMachineName(machine) || machine.type?.name || ""}
              </div>
            </div>
            {/* Top-right cluster. The live grid stacks the NEW badge and the
                health dot in the SAME absolute corner as the kebab; laying all
                three out as one flex row is the v2 rebuild and cannot overlap. */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              {isNew && <NewBadge />}
              {!decommissioned && (
                <HealthDot openReports={machine.openServiceReportsCount} />
              )}
              <FleetCardMenu
                actions={actions}
                ariaLabel={translate("myMachinery:v2.cardMenu")}
              />
            </div>
          </div>

          {location && !decommissioned && (
            <div
              style={{
                display: "inline-flex", alignItems: "center", gap: 5, marginTop: 8,
                fontSize: 11.5, color: "var(--ink-2)", maxWidth: "100%", minWidth: 0,
              }}
            >
              <span style={{ color: "var(--mod-accent)", display: "inline-flex" }}>
                <PinGlyph />
              </span>
              <span
                style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {location}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Status + connectivity row — omitted ENTIRELY when unconnected (§4.3). */}
      {p.showStatusRow && (
        <div
          style={{
            margin: "0 16px 12px", padding: "10px 0 0",
            borderTop: "1px solid var(--border-soft)", display: "flex",
            alignItems: "center", gap: 10, flexWrap: "wrap",
          }}
        >
          {p.showAwaitingBadge ? (
            <AwaitingBadge />
          ) : (
            <StatusBadge status={p.badgeStatus} stale={p.staleLook} variant="chip" />
          )}
          <span style={{ flex: 1 }} />
          <ConnDot state={p.dotConn} lastHeartbeat={telemetry.heartbeat} />
        </div>
      )}

      {/* Identity grid */}
      <div
        style={{
          margin: "0 16px", padding: p.showStatusRow ? "0 0 12px" : "8px 0 12px",
          borderTop: p.showStatusRow ? "0" : "1px solid var(--border-soft)",
          display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 8, columnGap: 16,
          minWidth: 0,
        }}
      >
        <KV label={translate("myMachinery:sn")} value={machine.serialNumber} mono />
        <KV label={translate("myMachinery:machine")} value={machine.internalNumber} mono />
        <KV label={translate("myMachinery:brand")} value={getBrandDisplayName(machine)} />
        <KV
          label={translate("myMachinery:system")}
          value={machine.controlSystemBrand?.name || machine.system}
          sub={machine.controlSystemBrand?.name ? machine.system : null}
        />
        {year && <KV label={translate("myMachinery:productionYear")} value={year} mono />}
        {machine.maxSpindleRpm != null && (
          <KV
            label={translate("myMachinery:rpm")}
            value={machine.maxSpindleRpm.toLocaleString()}
            mono
          />
        )}
      </div>

      <FleetCardChips
        machine={machine}
        snMissing={snMissing}
        decommissioned={decommissioned}
      />
      {decommissioned && <FleetCardDecommissionNote machine={machine} />}

      {/* Exactly ONE footer, chosen by the contract. */}
      {alarm ? (
        <AlarmStrip
          code={alarm.code}
          message={alarm.message}
          since={alarm.since}
          lastKnown={p.alarmLastKnown}
        />
      ) : p.showProgramLine && telemetry.program ? (
        <ProgramLine
          program={telemetry.program}
          partCount={telemetry.partCount}
          spindleRpm={telemetry.spindleRpm}
          feedPercent={telemetry.feedPercent}
        />
      ) : p.showNoTelemetry && !decommissioned ? (
        <NoTelemetryFooter />
      ) : null}
    </article>
  );
};

export default FleetCard;
